import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PersonalDataLabel,
  PersonalDataValue,
  PropertyLabel,
  PropertyValue,
} from 'constants/objectManager.constants';
import { getInlineString, getText } from 'i18n/intl';
import { JobObjectSelection } from 'pages/objectManager/hooks/useJobSubmit';
import type { BatchEditOperation } from 'types/batchEditObject.type';
import { EPersonalInformation, EProperty, type TType } from '@timeedit/types';
import { Form, Modal, Typography } from '@timeedit/ui-components/antd';
import BulkEditCategory from './BulkEditCategory';
import BulkEditCheckbox from './BulkEditCheckbox';
import { useBulkEditHasSelection, useBulkEditSubmit } from './common';
import './BulkEditField.scss';

type Props = {
  setOpen: (openSection: 'common' | false) => void;
  open: boolean;
  selectedObjectsCount?: number;
  objectType: TType | null;
  jobObjectSelection: JobObjectSelection;
  onSubmitSuccess?: () => void;
};

const INDEXED_PROPERTIES = {
  property: { id: 0, name: 'property' },
  personalInformation: { id: 1, name: 'personalInformation' },
  types: { id: 2, name: 'types' },
  isActive: { id: 3, name: 'isActive' },
};

const BulkEditGeneralInfoModal = ({
  open,
  setOpen,
  selectedObjectsCount,
  objectType,
  jobObjectSelection,
  onSubmitSuccess,
}: Props) => {
  const [sendingBatch, setSendingBatch] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const buildOperations = useCallback(
    (validationResult: Record<string, any>) =>
      transformFormDataToBatchEditOperation(validationResult),
    []
  );

  const unavailableOptions = useMemo(() => {
    const options = [];
    if (!objectType) return options;

    if (!objectType.allowAbstractObjects) {
      options.push(PropertyLabel[EProperty.ABSTRACT]);
      options.push(PropertyLabel[EProperty.VIRTUAL_ABSTRACT]);
    }
    if (!objectType.allowVirtualObjects) {
      options.push(PropertyLabel[EProperty.VIRTUAL_ABSTRACT]);
      options.push(PropertyLabel[EProperty.VIRTUAL_STANDARD]);
    }
    if (!objectType.allowStandardObjects) {
      options.push(PropertyLabel[EProperty.STANDARD]);
      options.push(PropertyLabel[EProperty.TEMPLATE_OBJECT]);
    }

    return options;
  }, [objectType]);

  const onSubmit = useBulkEditSubmit({
    form,
    setSendingBatch,
    setOpen,
    selection: jobObjectSelection,
    operationType: 'EDIT_GENERAL_INFO',
    buildOperations,
    successMessage: getText('object_manager.bulk-edit-modal.success-message'),
    onSuccess: onSubmitSuccess,
  });

  const infoSections = useMemo(
    () => [
      <BulkEditCategory
        key="property"
        actionList={['keep', 'findReplace', 'replaceAll']}
        field={{
          id: INDEXED_PROPERTIES.property.id,
          name: getText(
            'object_manager.bulk-edit-modal.general.property.label'
          ),
          multiple: false,
          unique: undefined,
          mandatory: undefined,
          editable: false,
          fieldType: 'CATEGORY',
          categories: Object.entries(EProperty).map(
            ([_, value]) => PropertyLabel[value]
          ),
          disabledCategories: unavailableOptions,
          description: undefined,
          min: undefined,
          max: undefined,
          length: undefined,
        }}
        form={form}
      />,
      <BulkEditCategory
        key="personalInformation"
        actionList={['keep', 'findReplace', 'replaceAll']}
        field={{
          id: INDEXED_PROPERTIES.personalInformation.id,
          name: getText(
            'object_manager.bulk-edit-modal.general.personal-information.label'
          ),
          multiple: false,
          unique: undefined,
          mandatory: undefined,
          editable: false,
          fieldType: 'CATEGORY',
          categories: Object.entries(EPersonalInformation).map(
            ([_, value]) => PersonalDataLabel[value]
          ),
          description: undefined,
          min: undefined,
          max: undefined,
          length: undefined,
        }}
        form={form}
      />,
      <BulkEditCheckbox
        key="isActive"
        form={form}
        field={{
          id: INDEXED_PROPERTIES.isActive.id,
          name: getText(
            'object_manager.bulk-edit-modal.general.is-active.label'
          ),
          multiple: false,
          editable: false,
          fieldType: 'BOOLEAN',
          description: undefined,
          min: undefined,
          max: undefined,
          categories: undefined,
          length: undefined,
          mandatory: undefined,
          unique: undefined,
        }}
      />,
    ],
    [unavailableOptions]
  );

  const hasSelection = useBulkEditHasSelection(form);

  if (!objectType) {
    return null;
  }

  return (
    <Modal
      open={open}
      okText={getText('object_manager.bulk-edit-modal.okText')}
      onOk={onSubmit}
      cancelText={getText('cancel')}
      onCancel={() => setOpen(false)}
      okButtonProps={{
        loading: sendingBatch,
        disabled: hasSelection === false,
      }}
      title={
        <div className="bulk-edit-modal-title-wrapper">
          {getText('object_manager.bulk-edit-modal.general.title')}
          <Typography.Text>
            {getInlineString(
              'object_manager.bulk-edit-modal.subtitle',
              selectedObjectsCount,
              objectType.name
            )}
          </Typography.Text>
        </div>
      }
    >
      <Form form={form} layout="horizontal" requiredMark={false}>
        <div className="bulk-edit-wrapper">{infoSections}</div>
      </Form>
    </Modal>
  );
};

const transformFormDataToBatchEditOperation = (
  formData: Record<string, any>
): BatchEditOperation[] => {
  const operations: BatchEditOperation[] = [];

  if (
    formData[INDEXED_PROPERTIES.property.id] &&
    formData[INDEXED_PROPERTIES.property.id] !== 'keep'
  ) {
    if (formData[INDEXED_PROPERTIES.property.id] === 'findReplace') {
      const searchValue = formData[`${INDEXED_PROPERTIES.property.id}_replace`];
      operations.push({
        category: 'generalInfo',
        attribute: 'property',
        operation: formData[INDEXED_PROPERTIES.property.id],
        searchValue: Array.isArray(searchValue)
          ? searchValue.map((id) => PropertyValue[id])
          : PropertyValue[
              formData[`${INDEXED_PROPERTIES.property.id}_replace`]
            ],
        value:
          PropertyValue[formData[`${INDEXED_PROPERTIES.property.id}_with`]],
      });
    } else if (formData[INDEXED_PROPERTIES.property.id] === 'replaceAll') {
      const replaceValue =
        formData[`${INDEXED_PROPERTIES.property.id}_replaceAll`];
      operations.push({
        category: 'generalInfo',
        attribute: 'property',
        operation: formData[INDEXED_PROPERTIES.property.id],
        value: PropertyValue[replaceValue],
      });
    }
  }

  if (
    formData[INDEXED_PROPERTIES.personalInformation.id] &&
    formData[INDEXED_PROPERTIES.personalInformation.id] !== 'keep'
  ) {
    if (formData[INDEXED_PROPERTIES.personalInformation.id] === 'findReplace') {
      const searchValue =
        formData[`${INDEXED_PROPERTIES.personalInformation.id}_replace`];
      operations.push({
        category: 'generalInfo',
        attribute: 'personalInformation',
        operation: formData[INDEXED_PROPERTIES.personalInformation.id],
        searchValue: Array.isArray(searchValue)
          ? searchValue.map((id) => PersonalDataValue[id])
          : PersonalDataValue[searchValue],
        value:
          PersonalDataValue[
            formData[`${INDEXED_PROPERTIES.personalInformation.id}_with`]
          ],
      });
    } else if (
      formData[INDEXED_PROPERTIES.personalInformation.id] === 'replaceAll'
    ) {
      const replaceValue =
        formData[`${INDEXED_PROPERTIES.personalInformation.id}_replaceAll`];
      operations.push({
        category: 'generalInfo',
        attribute: 'personalInformation',
        operation: formData[INDEXED_PROPERTIES.personalInformation.id],
        value: PersonalDataValue[replaceValue],
      });
    }
  }

  if (
    formData[INDEXED_PROPERTIES.isActive.id] &&
    formData[INDEXED_PROPERTIES.isActive.id] !== 'keep'
  ) {
    operations.push({
      category: 'generalInfo',
      attribute: 'isActive',
      operation: formData[INDEXED_PROPERTIES.isActive.id],
    });
  }

  return operations;
};

export default BulkEditGeneralInfoModal;
